import { PropsWithChildren, createContext, useState } from 'react'

import {
  Boarding,
  BoardingOption,
  Center,
  CenterItem,
  CenterState,
  ContextProps,
  ControlServiceResponse,
} from './types'
import { useStorage } from '../../../hooks/useStorage'

interface InitialData extends Omit<ContextProps, 'handleInitialDataCS' | 'handleResetData'> {}

export const ControlServicesContext = createContext({} as ContextProps)

export const ControlServicesProvider = ({ children }: PropsWithChildren) => {
  const [initialData, setInitialData] = useState<InitialData>({
    hasData: false,
    boardings: [],
    jobs: {},
    chores: {},
    centers: [],
    cages: {},
    modules: {},
  })

  const { isFirstRender, removeStorage } = useStorage({
    initialData,
    setDataFromStorage: (data) => setInitialData(data),
    key: 'aqua-data-controlService',
  })
  const handleInitialDataCS = (data: ControlServiceResponse) => {
    isFirstRender.current = false
    setInitialData({
      hasData: true,
      boardings: formatBoardings(data.boardings),
      jobs: formatJobs(data.boardings),
      chores: formatChores(data.boardings),
      centers: formatCenters(data.centers),
      cages: formatCage(data.centers),
      modules: formatModules(data.centers),
    })
  }

  const formatBoardings = (data: Boarding[]): BoardingOption[] =>
    data.map((boarding) => ({
      id: boarding.boardingId,
      name: boarding.name,
      category: boarding.service.category,
      serviceName: boarding.service.name,
      categoryFailure: boarding.service.categoryFailure,
      boardingName: boarding.name,
    }))

  const formatJobs = (data: Boarding[]) =>
    Object.fromEntries(
      data.map(({ boardingId, service }) => [
        boardingId,
        service.jobs.map((job) => ({ name: job.name, id: job.value })),
      ]),
    )

  const formatChores = (data: Boarding[]) =>
    Object.fromEntries(
      data.flatMap(({ service: { jobs } }) =>
        jobs.map(({ chores, value }) => [
          value,
          chores.map((chore) => ({ name: chore.name, id: chore.value })),
        ]),
      ),
    )

  const formatCenters = (data: Center[]): CenterItem[] =>
    data.map((center) => ({
      name: center.name,
      id: center.value,
      state: center.state as CenterState,
    }))

  const formatCage = (data: Center[]) =>
    Object.fromEntries(
      data.map(({ value, cages }) => [
        value,
        cages.map((cage) => ({ id: cage.value, name: cage.name })),
      ]),
    )
  const formatModules = (data: Center[]) =>
    Object.fromEntries(data.map(({ value, modules }) => [value, modules]))

  const handleResetData = async () => {
    removeStorage()
    setInitialData({
      hasData: false,
      boardings: [],
      jobs: {},
      chores: {},
      centers: [],
      cages: {},
      modules: {},
    })
  }
  return (
    <ControlServicesContext.Provider
      value={{
        ...initialData,
        handleInitialDataCS,
        handleResetData,
      }}
    >
      {children}
    </ControlServicesContext.Provider>
  )
}
